import { sample } from 'lodash-es';

import { distance } from 'utils/utils';
import { type Promotion } from 'schemas';

export const specialtyMatch = (
  promotions: Promotion[],
  specialtyId?: number,
  subspecialtyId?: number
): Promotion[] => {
  const matches: Promotion[] = [];
  promotions.forEach((promotion) => {
    // Always match on subspecialtyId if present
    // If there is a subspecialty match here, hasParentSpecialtyMatch will always be true.
    if (subspecialtyId) {
      const hasSubspecialtyMatch = promotion.subspecialties?.some(
        (subspecialty) => subspecialty.subspecialtyId === subspecialtyId
      );

      if (hasSubspecialtyMatch) {
        matches.push(promotion);
      }
    }

    // We only want to include specialty matches that do not have any related subspecialties on the promotion.
    // For subspecialty searches we also pass the parent specialtyId to this function.
    // If there is a subspecialty on the promotion that has the parentSpecialtyId of the current search, we always want to filter on subspecialty.
    const hasSpecialtyMatch = promotion.specialties?.some(
      (specialty) => specialty.specialtyId === specialtyId
    );

    const hasParentSpecialtyMatch = promotion.subspecialties?.some(
      (subspecialty) => subspecialty.parentSpecialtyId === specialtyId
    );

    if (hasSpecialtyMatch && !hasParentSpecialtyMatch) {
      matches.push(promotion);
    }
  });

  return matches;
};

export const keepOnlyHighest = (arr: Promotion[], key: keyof Promotion): Promotion[] => {
  const sorted = arr.sort((a, b) => {
    if (typeof a[key] === 'number' && typeof b[key] === 'number') {
      return b[key] - a[key];
    }
    if (typeof a[key] === 'string' && typeof b[key] === 'string') {
      return b[key].localeCompare(a[key]);
    }
    return 0;
  });
  const highestValue = sorted[0][key];
  return sorted.filter((a) => a[key] === highestValue);
};

export const sortFilterByWeight = (promotions: Promotion[]): Promotion[] =>
  keepOnlyHighest(promotions, 'weight');

interface PromotionWithDistance extends Promotion {
  locations: (Promotion['locations'][number] & { distanceInMiles?: number })[];
}

/**
 * Coords must be either both numbers or both null/undefined (no mixing).
 */
type AddDistanceCoords =
  | [globalLat: number, globalLong: number]
  | [globalLat: null | undefined, globalLong: null | undefined];

export function addDistanceValuesAndSort(
  promotions: Promotion[],
  ...coords: AddDistanceCoords
): Promotion[] | PromotionWithDistance[] {
  const [globalLat, globalLong] = coords;
  if (globalLat == null || globalLong == null) return promotions;

  return promotions.map((promotion) => {
    if (promotion.locations?.length === 0) return promotion;

    return {
      ...promotion,
      locations: promotion.locations
        .map((loc) => {
          if (!loc.latitude || !loc.longitude) return loc;
          const distanceInMiles = +distance(loc.latitude, loc.longitude, globalLat, globalLong);
          return {
            ...loc,
            ...(!Number.isNaN(distanceInMiles) && { distanceInMiles }),
          };
        })
        .sort((a, b) => {
          if (typeof a.distanceInMiles === 'number' && typeof b.distanceInMiles === 'number') {
            return a.distanceInMiles - b.distanceInMiles;
          }
          return 0;
        }),
    };
  });
}

export const getClosestPromotionsWithinRadius = (
  promotions: PromotionWithDistance[]
): Promotion[] => {
  // remove promotions with no locations and radius
  const withLocation = promotions.filter(
    (promotion) => promotion.locations.length > 0 && promotion.radius
  );
  // If there are no promotions with a location and radius, return all so we can move on to the next step
  if (withLocation.length === 0) {
    return promotions;
  }

  // Check to see if the users location is within the radius of each promotions first location (locations already ordered)
  const withinRadius = withLocation
    .filter((promotion) => {
      if (promotion.locations[0].distanceInMiles === undefined || promotion.radius === undefined)
        return false;
      return promotion.locations[0].distanceInMiles <= promotion.radius;
    })
    .filter((promotion) => promotion);
  // If the users location is not within the radius of any promotion locations, return all so we can move on to the next step
  if (withinRadius.length === 0) {
    return promotions;
  }
  if (withinRadius.length === 1) {
    return withinRadius;
  }
  // If there are multiple promotions within radius, sort by distance of first location (locations already ordered)
  withinRadius.sort((a, b) => {
    if (
      a.locations[0].distanceInMiles === undefined ||
      b.locations[0].distanceInMiles === undefined
    )
      return 0;
    return a.locations[0].distanceInMiles - b.locations[0].distanceInMiles;
  });
  const shortestDistance = withinRadius[0].locations[0].distanceInMiles;
  return withinRadius.filter(
    (promotion) => promotion.locations[0].distanceInMiles === shortestDistance
  );
};

interface BestMatchingPromotionOptions {
  specialtyId?: number;
  subspecialtyId?: number;
}

/**
 * This function accepts an array of promotions and determines the single promotion in the array that best matches the criteria provided by the options.
 * */
export function findBestMatchingPromotion(
  promotions: Promotion[],
  options: BestMatchingPromotionOptions = {}
): Promotion | null {
  // return if no promotions were passed
  if (!Array.isArray(promotions) || promotions.length === 0) {
    return null;
  }

  const { specialtyId, subspecialtyId } = options;

  // 1 & 2. subspecialty and specialty match
  const specialtyMatchPromotions = specialtyMatch(promotions, specialtyId, subspecialtyId);
  if (specialtyMatchPromotions.length === 0) {
    return null;
  }
  if (specialtyMatchPromotions.length === 1) {
    return specialtyMatchPromotions[0];
  }

  // 3. primary override
  const primaryOverride = specialtyMatchPromotions.find((promotion) => promotion.isPrimary);
  if (primaryOverride) {
    return primaryOverride;
  }

  // 4. radius and distance
  const sortedByDistance = getClosestPromotionsWithinRadius(specialtyMatchPromotions);
  if (sortedByDistance.length === 1) {
    return sortedByDistance[0];
  }

  // 5. weight
  const highestWeightedPromotions = sortFilterByWeight(sortedByDistance);
  if (highestWeightedPromotions.length === 1) {
    return highestWeightedPromotions[0];
  }

  // multiple promotions are equally close
  // 6. randomly choose one
  return sample(highestWeightedPromotions) ?? null;
}
